import React, { useEffect, useMemo, useState } from "react";
import { auth, db, storage } from "../firebaseConfig";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { getDownloadURL, ref as sRef, uploadBytes } from "firebase/storage";

const DIAS = [
  "lunes",
  "martes",
  "miercoles",
  "jueves",
  "viernes",
  "sabado",
  "domingo",
];

function emptyDireccion() {
  return {
    calleNumero: "",
    ciudad: "",
    colonia: "",
    cp: "",
    dir2: "",
    estado: "",
  };
}

function emptyHorarioDia() {
  return {
    abierto: false,
    comida: false,
    comidaIni: "",
    comidaFin: "",
    ini: "",
    fin: "",
  };
}

function emptyHorario() {
  const out = {};
  for (const dia of DIAS) out[dia] = emptyHorarioDia();
  return out;
}

function normalizeDireccion(v) {
  return {
    calleNumero: v?.calleNumero || "",
    ciudad: v?.ciudad || "",
    colonia: v?.colonia || "",
    cp: v?.cp || "",
    dir2: v?.dir2 || "",
    estado: v?.estado || "",
  };
}

function normalizeHorario(h) {
  const base = emptyHorario();
  for (const dia of DIAS) {
    base[dia] = {
      abierto: Boolean(h?.[dia]?.abierto),
      comida: Boolean(h?.[dia]?.comida),
      comidaIni: h?.[dia]?.comidaIni || "",
      comidaFin: h?.[dia]?.comidaFin || "",
      ini: h?.[dia]?.ini || "",
      fin: h?.[dia]?.fin || "",
    };
  }
  return base;
}

function formatDireccion(d) {
  if (!d) return "Sin dirección";
  const parts = [
    d.calleNumero,
    d.colonia,
    d.ciudad,
    d.estado,
    d.cp ? `CP ${d.cp}` : "",
    d.dir2,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "Sin dirección";
}

function cloneData(data) {
  return JSON.parse(JSON.stringify(data));
}

export default function Dashboard() {
  const uid = auth?.currentUser?.uid || null;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  const [userDocId, setUserDocId] = useState("");
  const [originalData, setOriginalData] = useState(null);
  const [form, setForm] = useState(null);

  const [perfilFile, setPerfilFile] = useState(null);
  const [perfilPreview, setPerfilPreview] = useState("");

  const [tiendaFile, setTiendaFile] = useState(null);
  const [tiendaPreview, setTiendaPreview] = useState("");
  const isMobile = typeof window !== "undefined" ? window.innerWidth < 900 : false;

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");
        setOkMsg("");

        if (!uid) throw new Error("No hay sesión iniciada.");

        const ref = doc(db, "usuarios", uid);
        const snap = await getDoc(ref);

        if (!snap.exists()) throw new Error("No se encontró usuario.");

        const data = snap.data() || {};

        const normalized = {
          nombre: data?.nombre || "",
          telefono: data?.telefono || "",
          fotoPerfil: data?.fotoPerfil || "",
          correo: data?.correo || "",
          direccion: normalizeDireccion(data?.direccion),

          telefonoTienda: data?.telefonoTienda || "",
          telefonoTienda2: data?.telefonoTienda2 || "",
          nombreTienda: data?.nombreTienda || "",
          solicitudVentaEstado: data?.solicitudVentaEstado || "",
          stripeAccountId: data?.stripeAccountId || "",
          descripcionTienda: data?.descripcionTienda || "",
          bannerTienda: data?.bannerTienda || "",
          direccionTienda: normalizeDireccion(data?.direccionTienda),
          horario: normalizeHorario(data?.horario),

          // extras por si ya existieran
          fotoPerfilOld: data?.fotoPerfil || "",
          bannerTiendaOld: data?.bannerTienda || "",
        };

        if (!alive) return;

        setUserDocId(uid);
        setOriginalData(cloneData(normalized));
        setForm(cloneData(normalized));
        setPerfilPreview(normalized.fotoPerfil || "");
        setTiendaPreview(normalized.bannerTienda || "");
      } catch (e) {
        console.error(e);
        if (alive) setError(e?.message || "No se pudo cargar la información.");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [uid]);

  const clienteDireccionTexto = useMemo(
    () => formatDireccion(form?.direccion),
    [form?.direccion]
  );

  const tiendaDireccionTexto = useMemo(
    () => formatDireccion(form?.direccionTienda),
    [form?.direccionTienda]
  );

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function setDireccionField(scope, field, value) {
    setForm((prev) => ({
      ...prev,
      [scope]: {
        ...(prev?.[scope] || emptyDireccion()),
        [field]: value,
      },
    }));
  }

  function setHorarioField(dia, field, value) {
    setForm((prev) => ({
      ...prev,
      horario: {
        ...(prev?.horario || {}),
        [dia]: {
          ...(prev?.horario?.[dia] || emptyHorarioDia()),
          [field]: value,
        },
      },
    }));
  }

  function onEdit() {
    setEditing(true);
    setError("");
    setOkMsg("");
  }

  function onCancel() {
    setEditing(false);
    setError("");
    setOkMsg("");
    setForm(cloneData(originalData));
    setPerfilFile(null);
    setTiendaFile(null);
    setPerfilPreview(originalData?.fotoPerfil || "");
    setTiendaPreview(originalData?.bannerTienda || "");
  }

  async function uploadImageFixed(file, path) {
    const fileRef = sRef(storage, path);
    await uploadBytes(fileRef, file, { contentType: file.type || "image/jpeg" });
    return await getDownloadURL(fileRef);
  }

  function validate() {
    if (!uid) return "No hay sesión iniciada.";
    if (!form?.nombre?.trim()) return "Falta el nombre del cliente.";
    if (!form?.correo?.trim()) return "Falta el correo.";
    return "";
  }

  async function onSave() {
    try {
      setError("");
      setOkMsg("");

      const msg = validate();
      if (msg) {
        setError(msg);
        return;
      }

      setSaving(true);

      let fotoPerfilUrl = form?.fotoPerfil || "";
      let bannerTiendaUrl = form?.bannerTienda || "";

      if (perfilFile) {
        fotoPerfilUrl = await uploadImageFixed(perfilFile, `usuarios/${uid}/perfil.jpg`);
      }

      if (tiendaFile) {
        bannerTiendaUrl = await uploadImageFixed(tiendaFile, `usuarios/${uid}/tienda.jpg`);
      }

      const payload = {
        nombre: form?.nombre?.trim() || "",
        telefono: form?.telefono?.trim() || "",
        fotoPerfil: fotoPerfilUrl,
        direccion: {
          calleNumero: form?.direccion?.calleNumero?.trim() || "",
          ciudad: form?.direccion?.ciudad?.trim() || "",
          colonia: form?.direccion?.colonia?.trim() || "",
          cp: form?.direccion?.cp?.trim() || "",
          dir2: form?.direccion?.dir2?.trim() || "",
          estado: form?.direccion?.estado?.trim() || "",
        },

        telefonoTienda: form?.telefonoTienda?.trim() || "",
        telefonoTienda2: form?.telefonoTienda2?.trim() || "",
        nombreTienda: form?.nombreTienda?.trim() || "",
        descripcionTienda: form?.descripcionTienda?.trim() || "",
        bannerTienda: bannerTiendaUrl,
        direccionTienda: {
          calleNumero: form?.direccionTienda?.calleNumero?.trim() || "",
          ciudad: form?.direccionTienda?.ciudad?.trim() || "",
          colonia: form?.direccionTienda?.colonia?.trim() || "",
          cp: form?.direccionTienda?.cp?.trim() || "",
          dir2: form?.direccionTienda?.dir2?.trim() || "",
          estado: form?.direccionTienda?.estado?.trim() || "",
        },
        horario: normalizeHorario(form?.horario),
      };

      await updateDoc(doc(db, "usuarios", uid), payload);

      const updated = {
        ...cloneData(form),
        fotoPerfil: fotoPerfilUrl,
        bannerTienda: bannerTiendaUrl,
      };

      setOriginalData(cloneData(updated));
      setForm(cloneData(updated));
      setPerfilPreview(fotoPerfilUrl || "");
      setTiendaPreview(bannerTiendaUrl || "");
      setPerfilFile(null);
      setTiendaFile(null);
      setEditing(false);
      setOkMsg("Datos guardados correctamente.");
    } catch (e) {
      console.error(e);
      setError(e?.message || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  }

  if (loading || !form) {
    return (
      <div style={S.page}>
        <div style={S.wrapper}>
          <div style={S.heroCard}>
            <div style={S.title}>Dashboard</div>
            <div style={S.sub}>Cargando información del usuario...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.wrapper}>
        <div style={S.heroCard}>
          <div>
            <div style={S.title}>Dashboard de usuario</div>
            <div style={S.sub}>
              Información separada en cliente y tienda, con edición controlada.
            </div>
          </div>

          {!editing ? (
            <button style={S.primaryBtn} type="button" onClick={onEdit}>
              Editar
            </button>
          ) : (
            <div style={S.btnRow}>
              <button
                style={S.secondaryBtn}
                type="button"
                onClick={onCancel}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                style={S.primaryBtn}
                type="button"
                onClick={onSave}
                disabled={saving}
              >
                {saving ? "Guardando..." : "Guardar"}
              </button>
            </div>
          )}
        </div>

        {error ? (
          <div style={S.errorBox}>{error}</div>
        ) : null}

        {okMsg ? (
          <div style={S.okBox}>{okMsg}</div>
        ) : null}

        <div
            style={{
              ...S.grid,
              gridTemplateColumns: isMobile ? "1fr" : S.grid.gridTemplateColumns,
            }}
          >
          <section style={{ ...S.card, ...S.cardCliente }}>
            <div style={S.cardTitle}>Cliente</div>

            <div style={S.photoRow}>
              <div style={S.avatarWrap}>
                {perfilPreview ? (
                  <img src={perfilPreview} alt="foto perfil" style={S.avatar} />
                ) : (
                  <div style={S.avatarPlaceholder}>Sin foto</div>
                )}
              </div>

              {editing ? (
                <div style={{ flex: 1 }}>
                  <label style={S.label}>Foto de perfil</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      setPerfilFile(f);
                      if (f) setPerfilPreview(URL.createObjectURL(f));
                    }}
                  />
                  <div style={S.hint}>Se reemplaza en storage: usuarios/{uid}/perfil.jpg</div>
                </div>
              ) : null}
            </div>

            <div style={S.infoGrid}>
              <Field label="Nombre">
                {editing ? (
                  <input
                    style={S.input}
                    value={form.nombre}
                    onChange={(e) => setField("nombre", e.target.value)}
                  />
                ) : (
                  <InfoValue value={form.nombre} />
                )}
              </Field>

              <Field label="ID">
                <InfoValue value={userDocId} />
              </Field>

              <Field label="Teléfono">
                {editing ? (
                  <input
                    style={S.input}
                    value={form.telefono}
                    onChange={(e) => setField("telefono", e.target.value)}
                  />
                ) : (
                  <InfoValue value={form.telefono} />
                )}
              </Field>

              <Field label="Correo">
                <InfoValue value={form.correo} />
              </Field>
            </div>

            <div style={S.blockTitle}>Dirección del cliente</div>

            {!editing ? (
              <div style={S.readBox}>{clienteDireccionTexto}</div>
            ) : (
              <DireccionEditor
                value={form.direccion}
                onChange={(field, value) => setDireccionField("direccion", field, value)}
              />
            )}
          </section>

          <section style={{ ...S.card, ...S.cardTienda }}>
            <div style={S.cardTitle}>Tienda</div>

            <div style={S.bannerWrap}>
              {tiendaPreview ? (
                <img src={tiendaPreview} alt="banner tienda" style={S.bannerImg} />
              ) : (
                <div style={S.bannerPlaceholder}>Sin banner de tienda</div>
              )}
            </div>

            {editing ? (
              <div style={{ marginTop: 12 }}>
                <label style={S.label}>Banner de tienda</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setTiendaFile(f);
                    if (f) setTiendaPreview(URL.createObjectURL(f));
                  }}
                />
                <div style={S.hint}>Se reemplaza en storage: usuarios/{uid}/tienda.jpg</div>
              </div>
            ) : null}

            <div style={S.infoGrid}>
              <Field label="Nombre tienda">
                {editing ? (
                  <input
                    style={S.input}
                    value={form.nombreTienda}
                    onChange={(e) => setField("nombreTienda", e.target.value)}
                  />
                ) : (
                  <InfoValue value={form.nombreTienda} />
                )}
              </Field>

              <Field label="Solicitud Vendedor">
                <InfoValue value={form.solicitudVentaEstado} />
              </Field>

              <Field label="Teléfono tienda">
                {editing ? (
                  <input
                    style={S.input}
                    value={form.telefonoTienda}
                    onChange={(e) => setField("telefonoTienda", e.target.value)}
                  />
                ) : (
                  <InfoValue value={form.telefonoTienda} />
                )}
              </Field>

              <Field label="Teléfono tienda 2">
                {editing ? (
                  <input
                    style={S.input}
                    value={form.telefonoTienda2}
                    onChange={(e) => setField("telefonoTienda2", e.target.value)}
                  />
                ) : (
                  <InfoValue value={form.telefonoTienda2} />
                )}
              </Field>

              <Field label="Stripe Account ID">
                <InfoValue value={form.stripeAccountId} />
              </Field>

              <Field label="Descripción tienda" full>
                {editing ? (
                  <textarea
                    style={{ ...S.input, minHeight: 110, resize: "vertical" }}
                    value={form.descripcionTienda}
                    onChange={(e) => setField("descripcionTienda", e.target.value)}
                  />
                ) : (
                  <div style={S.readBox}>{form.descripcionTienda || "Sin descripción"}</div>
                )}
              </Field>
            </div>

            <div style={S.blockTitle}>Dirección de la tienda</div>

            {!editing ? (
              <div style={S.readBox}>{tiendaDireccionTexto}</div>
            ) : (
              <DireccionEditor
                value={form.direccionTienda}
                onChange={(field, value) => setDireccionField("direccionTienda", field, value)}
              />
            )}

            <div style={S.blockTitle}>Horario</div>

            <div style={S.horarioList}>
              {DIAS.map((dia) => {
                const h = form?.horario?.[dia] || emptyHorarioDia();

                return (
                  <div key={dia} style={S.horarioCard}>
                    <div style={S.horarioHead}>
                      <div style={S.horarioDia}>{capitalize(dia)}</div>
                    </div>

                    {!editing ? (
                      <HorarioPreview diaData={h} />
                    ) : (
                      <div style={S.horarioEditGrid}>
                        <label style={S.checkLabel}>
                          <input
                            type="checkbox"
                            checked={h.abierto}
                            onChange={(e) => setHorarioField(dia, "abierto", e.target.checked)}
                          />
                          <span>Abierto</span>
                        </label>

                        <label style={S.checkLabel}>
                          <input
                            type="checkbox"
                            checked={h.comida}
                            onChange={(e) => setHorarioField(dia, "comida", e.target.checked)}
                          />
                          <span>Tiene comida</span>
                        </label>

                        <div style={S.twoCols}>
                          <MiniField label="Inicio">
                            <input
                              style={S.input}
                              value={h.ini}
                              onChange={(e) => setHorarioField(dia, "ini", e.target.value)}
                              placeholder="09:00"
                            />
                          </MiniField>

                          <MiniField label="Fin">
                            <input
                              style={S.input}
                              value={h.fin}
                              onChange={(e) => setHorarioField(dia, "fin", e.target.value)}
                              placeholder="18:00"
                            />
                          </MiniField>
                        </div>

                        <div style={S.twoCols}>
                          <MiniField label="Comida inicio">
                            <input
                              style={S.input}
                              value={h.comidaIni}
                              onChange={(e) => setHorarioField(dia, "comidaIni", e.target.value)}
                              placeholder="14:00"
                              disabled={!h.comida}
                            />
                          </MiniField>

                          <MiniField label="Comida fin">
                            <input
                              style={S.input}
                              value={h.comidaFin}
                              onChange={(e) => setHorarioField(dia, "comidaFin", e.target.value)}
                              placeholder="15:00"
                              disabled={!h.comida}
                            />
                          </MiniField>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children, full = false }) {
  return (
    <div style={full ? { ...S.field, gridColumn: "1 / -1" } : S.field}>
      <div style={S.label}>{label}</div>
      {children}
    </div>
  );
}

function MiniField({ label, children }) {
  return (
    <div>
      <div style={S.miniLabel}>{label}</div>
      {children}
    </div>
  );
}

function InfoValue({ value }) {
  return <div style={S.valueBox}>{value || "—"}</div>;
}

function DireccionEditor({ value, onChange }) {
  return (
    <div style={S.dirGrid}>
      <Field label="Calle y número">
        <input
          style={S.input}
          value={value?.calleNumero || ""}
          onChange={(e) => onChange("calleNumero", e.target.value)}
        />
      </Field>

      <Field label="Colonia">
        <input
          style={S.input}
          value={value?.colonia || ""}
          onChange={(e) => onChange("colonia", e.target.value)}
        />
      </Field>

      <Field label="Ciudad">
        <input
          style={S.input}
          value={value?.ciudad || ""}
          onChange={(e) => onChange("ciudad", e.target.value)}
        />
      </Field>

      <Field label="Estado">
        <input
          style={S.input}
          value={value?.estado || ""}
          onChange={(e) => onChange("estado", e.target.value)}
        />
      </Field>

      <Field label="CP">
        <input
          style={S.input}
          value={value?.cp || ""}
          onChange={(e) => onChange("cp", e.target.value)}
        />
      </Field>

      <Field label="Dir2">
        <input
          style={S.input}
          value={value?.dir2 || ""}
          onChange={(e) => onChange("dir2", e.target.value)}
        />
      </Field>
    </div>
  );
}

function HorarioPreview({ diaData }) {
  if (!diaData?.abierto) {
    return <div style={S.readBox}>Cerrado</div>;
  }

  return (
    <div style={S.readBox}>
      <div>
        <b>Horario:</b> {diaData?.ini || "—"} - {diaData?.fin || "—"}
      </div>
      <div style={{ marginTop: 6 }}>
        <b>Comida:</b>{" "}
        {diaData?.comida
          ? `${diaData?.comidaIni || "—"} - ${diaData?.comidaFin || "—"}`
          : "No"}
      </div>
    </div>
  );
}

function capitalize(v) {
  if (!v) return "";
  return v.charAt(0).toUpperCase() + v.slice(1);
}

const S = {
  page: {
    padding: 16,
    background: "#f5f7fb",
    minHeight: "100vh",
  },
  cardCliente: {
    maxWidth: 420,
  },

  cardTienda: {
    minWidth: 0,
  },

  wrapper: {
    maxWidth: 1240,
    margin: "0 auto",
    display: "grid",
    gap: 16,
  },

  heroCard: {
    background: "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,255,0.98))",
    border: "1px solid rgba(15,23,42,0.08)",
    borderRadius: 22,
    padding: 18,
    boxShadow: "0 16px 50px rgba(15,23,42,0.08)",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },

  title: {
    fontSize: 26,
    fontWeight: 900,
    color: "#0f172a",
    letterSpacing: -0.4,
  },

  sub: {
    marginTop: 6,
    color: "#475569",
    fontSize: 14,
    lineHeight: 1.5,
  },

 grid: {
  display: "grid",
  gridTemplateColumns: "minmax(320px, 420px) minmax(0, 1fr)",
  gap: 16,
  alignItems: "start",
},

card: {
  background: "rgba(255,255,255,0.98)",
  border: "1px solid rgba(15,23,42,0.08)",
  borderRadius: 22,
  padding: 18,
  boxShadow: "0 16px 50px rgba(15,23,42,0.06)",
  display: "grid",
  gap: 14,
  alignSelf: "start",
},
  cardTitle: {
    fontSize: 18,
    fontWeight: 900,
    color: "#0f172a",
    marginBottom: 2,
  },

  blockTitle: {
    fontSize: 14,
    fontWeight: 900,
    color: "#1e293b",
    marginTop: 4,
  },

  photoRow: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },

  avatarWrap: {
    width: 120,
    height: 120,
    borderRadius: 999,
    overflow: "hidden",
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#eef2ff",
    flexShrink: 0,
  },

  avatar: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  avatarPlaceholder: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    color: "#64748b",
    fontWeight: 800,
    fontSize: 13,
  },

  bannerWrap: {
    width: "100%",
    height: 170,
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#eef2ff",
  },

  bannerImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  bannerPlaceholder: {
    width: "100%",
    height: "100%",
    display: "grid",
    placeItems: "center",
    color: "#64748b",
    fontWeight: 800,
  },

infoGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
},

  field: {
    display: "grid",
    gap: 8,
  },

  label: {
    fontSize: 12,
    fontWeight: 900,
    color: "#334155",
  },

  miniLabel: {
    fontSize: 11,
    fontWeight: 800,
    color: "#475569",
    marginBottom: 6,
  },

  input: {
    width: "100%",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    background: "#fff",
    fontSize: 14,
    outline: "none",
    color: "#0f172a",
    boxSizing: "border-box",
  },

  valueBox: {
    minHeight: 46,
    display: "flex",
    alignItems: "center",
    padding: "12px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#f8fafc",
    color: "#0f172a",
    fontSize: 14,
    wordBreak: "break-word",
  },

  readBox: {
    padding: 14,
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#f8fafc",
    color: "#0f172a",
    fontSize: 14,
    lineHeight: 1.5,
  },

dirGrid: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: 14,
},

  horarioList: {
    display: "grid",
    gap: 12,
  },

  horarioCard: {
    borderRadius: 16,
    border: "1px solid rgba(15,23,42,0.08)",
    background: "#fbfdff",
    padding: 14,
  },

  horarioHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },

  horarioDia: {
    fontWeight: 900,
    color: "#0f172a",
    fontSize: 14,
  },

  horarioEditGrid: {
    display: "grid",
    gap: 12,
  },

  checkLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    fontWeight: 800,
    color: "#334155",
  },

twoCols: {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
  gap: 12,
},

  btnRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  primaryBtn: {
    border: "none",
    borderRadius: 14,
    background: "linear-gradient(180deg, #2563eb, #1d4ed8)",
    color: "#fff",
    padding: "12px 16px",
    fontWeight: 900,
    fontSize: 14,
    cursor: "pointer",
    boxShadow: "0 12px 30px rgba(37,99,235,0.20)",
  },

  secondaryBtn: {
    border: "1px solid rgba(15,23,42,0.12)",
    borderRadius: 14,
    background: "#fff",
    color: "#0f172a",
    padding: "12px 16px",
    fontWeight: 900,
    fontSize: 14,
    cursor: "pointer",
  },

  errorBox: {
    background: "rgba(239,68,68,0.08)",
    border: "1px solid rgba(239,68,68,0.18)",
    color: "#991b1b",
    borderRadius: 16,
    padding: 14,
    fontWeight: 700,
  },

  okBox: {
    background: "rgba(34,197,94,0.08)",
    border: "1px solid rgba(34,197,94,0.18)",
    color: "#166534",
    borderRadius: 16,
    padding: 14,
    fontWeight: 700,
  },

  hint: {
    marginTop: 6,
    fontSize: 12,
    color: "#64748b",
  },
};